import React, { useContext, useRef, useMemo, useCallback } from "react";
import { ErrorComponent, LoadingComponent, useExecutionDataView } from "@gooddata/sdk-ui";
import { useEffect } from "react";
import { useState } from "react";
import { newAttributeSort } from "@gooddata/sdk-model";
import * as Md from "../../md/full";
import { useAppContext } from "../../contexts/AppContext";
import { utils, writeFile } from "xlsx";
import MeasuresContext, { flagServiceM } from "../../contexts/Measures";
import Exportbtn from "../Export/Exportbtn";
import UserContext, { settingsAliases } from "../../contexts/User";
import { TfiWorld } from "react-icons/tfi";
import { calculateAdjustment } from "../../utils/calculationUtils";
import { RecordSwitchParameter } from "../../routes/Vehicles";
import { BiSync } from "react-icons/bi";
import { ImFilePicture } from "react-icons/im";
import ImageViewer from "../ImageViewer";
import FiltersContext from "../../contexts/Filters";

const VATRules = (country, channel) => {
    if (["FR", "FF", "NL"].includes(country)) {
        return true;
    } else if (channel === "B2B") {
        return false;
    } else {
        return true;
    }
};

const Extrapfrom = (Version) => {
    return Version.split(">> Adjusted From : ");
};

const Tabheaders = [
    "Experteye Code",
    "Segment",
    "Energy",
    "Body Type",
    "PC/LCV",
    "Size",
    "Gearbox",
    "Drive",
    "Power (HP)",
    "Power (KW)",
    "CO2",
    "Date",
    "Valid until",
    "Interest Rate",
    "RV (Purchase option)",
    "RV% of List Price",
    "Total Credit Charges",
    "Finance/OEM Contribution",
    "Total Payable",
    "Maintenance",
    "Tyres",
    "Breakdown",
    "Replacement Car",
    "Insurance",
    "Financial Loss",
    "Deposit Adjustement",
];

// Columns with icons
const columnIcon = {
    Url: TfiWorld,
    Picture: ImFilePicture,
};

function transposeArrayOfObjects(array) {
    if (!array || array.length === 0) {
        return [];
    }
    // Get the attributes from the first object
    const attributes = Object.keys(array[0]);

    // Transpose the array
    const transposedArray = attributes.map((attribute) => {
        const values = array.map((obj) => obj[attribute]);
        return [attribute, ...values];
    });

    return transposedArray;
}

const MakeModelDetailedView = ({ selectedVehicles, filters, footnote }) => {
    const [data, setData] = useState([]);
    const { kpiSettings, IncServices } = useContext(UserContext);
    const [showspecs, setShowspecs] = useState(false);
    const [showoffers, setShowoffers] = useState(false);
    const [showadj, setShowadj] = useState(false);
    const [currentFilter, setCurrentFilter] = useState(filters);
    const contentRef = useRef(null);
    const scrollRef = useRef(null);
    const [contentWidth, setContentWidth] = useState(0);
    const [selectedPicURL, setSelectedPicURL] = useState(undefined);
    const { getCustlabelByCustId } = useAppContext();
    const { seriesBy, selectedAdjDeposit } = useContext(MeasuresContext);
    const { getFilterValue } = useContext(FiltersContext);

    useEffect(() => {
        setCurrentFilter(filters);
    }, [filters]);

    const { result, status } = useExecutionDataView({
        execution: {
            seriesBy: [...seriesBy, Md.Deposit.Avg, Md.Emissions.Avg, /*37*/ Md.OtrPrice.Avg, /*38*/ Md.Apr.Avg, /*39*/ Md.FinanceContrib.Avg, /*40*/ Md.TotalCharges.Avg, /*41*/ Md.TotalPayable.Avg, /*45*/ Md.FlMetallicPaint.Avg],
            slicesBy: [
                Md.Version,
                Md.Channel1,
                Md.CustId,
                Md.Ctryid,
                Md.VersionStd,
                Md.VehSegment,
                Md.Fuel,
                Md.BodyStyle,
                Md.VehType,
                Md.Size,
                Md.GearsPlus,
                Md.Drive,
                Md.Channel3,
                Md.Extraptype,
                Md.Duration,
                Md.Warning,
                Md.Url,
                Md.CorrMake,
                Md.CorrModel,
                Md.DateDatasets.DateOfScraping.Date.DdMmYyyy,
                Md.PictureURL,
            ],
            filters: currentFilter,
            sortBy: [newAttributeSort(Md.CorrMake, "asc"), newAttributeSort(Md.CorrModel, "asc")], // Sort by Make and Model
        },
    });
    const ctryid = getFilterValue("filterCountryId")[0];

    const processedData = useMemo(() => {
        if (result && status === "success") {
            const slices = result?.data()?.slices().toArray();
            if (slices?.length > 0) {
                let predefined = [];
                if (selectedAdjDeposit !== "Total Payable by month") {
                    predefined = slices.filter((slice) => slice.dataPoints()[2].rawValue !== null);
                } else {
                    predefined = slices;
                }
                const flat = predefined?.map((slice) => {
                    const services = {
                        "Metallic Paint": ["UK", "GB"].includes(ctryid) ? Number(slice.dataPoints()[45].rawValue) : 0,
                        Maintenance: Number(slice.dataPoints()[5].rawValue),
                        Tyres: Number(slice.dataPoints()[7].rawValue),
                        Breakdown: Number(slice.dataPoints()[9].rawValue),
                        Replacement: Number(slice.dataPoints()[11].rawValue),
                        Insurance: Number(slice.dataPoints()[13].rawValue),
                        "Financial Loss": Number(slice.dataPoints()[15].rawValue),
                        "Winter Tyres": Number(slice.dataPoints()[17].rawValue),
                        [ctryid === "FR" ? "CertyNergy" : "EV Bonus"]: Number(slice.dataPoints()[19].rawValue),
                        "Co2 Malus": Number(slice.dataPoints()[21].rawValue),
                        ...(ctryid === "BE" && {
                            "Damage Insurance": slice.dataPoints()[35].rawValue !== null ? Number(slice.dataPoints()[35].rawValue) : 0,
                            "TPL Insurance": slice.dataPoints()[37].rawValue !== null ? Number(slice.dataPoints()[37].rawValue) : 0,
                        }),
                    };
                    const composer = {
                        flag_ser: kpiSettings["flag_ser"],
                        flag_tyr: kpiSettings["flag_tyr"],
                        flag_bre: kpiSettings["flag_bre"],
                        flag_rep: kpiSettings["flag_rep"],
                        flag_ins: kpiSettings["flag_ins"],
                        flag_fin: kpiSettings["flag_fin"],
                        flag_win: kpiSettings["flag_win"],
                        flag_co2: kpiSettings["flag_co2"],
                        flag_evb: kpiSettings["flag_evb"],
                        flag_insuranced: kpiSettings["flag_insuranced"],
                        flag_insurancetpl: kpiSettings["flag_insurancetpl"],
                    };
                    return {
                        // stand alone
                        Make: slice.sliceTitles()[17],
                        Model: slice.sliceTitles()[18],
                        Version: Extrapfrom(slice.sliceTitles()[0])[0],
                        Player: getCustlabelByCustId(slice.sliceTitles()[2]),
                        Channel: slice.sliceTitles()[1],
                        Country: slice.sliceTitles()[3],
                        "List Price": Number(slice.dataPoints()[1].rawValue) > 0 ? Number(Number(slice.dataPoints()[1].rawValue).toFixed(0)) : null,
                        "ListPrice VAT": VATRules(slice.sliceTitles()[3], slice.sliceTitles()[1]) ? "Included" : "Excluded",
                        "OTR Price": Number(slice.dataPoints()[40].rawValue) > 0 ? Number(Number(slice.dataPoints()[40].rawValue).toFixed(0)) : null,
                        Specifications: "",
                        "Experteye Code": slice.sliceTitles()[4],
                        Segment: slice.sliceTitles()[5],
                        Energy: slice.sliceTitles()[6],
                        "Body Type": slice.sliceTitles()[7],
                        "PC/LCV": slice.sliceTitles()[8],
                        Size: slice.sliceTitles()[9],
                        Gearbox: slice.sliceTitles()[10],
                        Drive: slice.sliceTitles()[11],
                        "Power (HP)": Number(slice.dataPoints()[22].rawValue) > 0 ? Number(Number(slice.dataPoints()[22].rawValue).toFixed(0)) : null,
                        "Power (KW)": Number(slice.dataPoints()[29].rawValue) > 0 ? Number(Number(slice.dataPoints()[29].rawValue).toFixed(0)) : null,
                        CO2: Number(slice.dataPoints()[39].rawValue) > 0 ? Number(Number(slice.dataPoints()[39].rawValue).toFixed(0)) : null,
                        "Product Type": slice.sliceTitles()[12],
                        Quote: slice.sliceTitles()[13],
                        Duration: Number(slice.sliceTitles()[14].split("/")[0]), // Duration
                        "Mileage Total": Number(slice.sliceTitles()[14].split("/")[1]), // Mileage
                        // published
                        "Web Rental": Number(slice.dataPoints()[3].rawValue) > 0 ? Number(Number(slice.dataPoints()[3].rawValue).toFixed(0)) : null,
                        "Web Deposit": Number(slice.dataPoints()[38].rawValue) > 0 ? Number(Number(slice.dataPoints()[38].rawValue).toFixed(0)) : null,
                        "Offer details": "",
                        Date: slice.sliceTitles()[19],
                        "Valid until": null,
                        "Interest Rate": slice.dataPoints()[41].rawValue === "0" ? 0 + "%" : Number(slice.dataPoints()[41].rawValue) > 0 ? Number(100 * Number(slice.dataPoints()[41].rawValue)).toFixed(2) + "%" : null,
                        "RV (Purchase option)": Number(slice.dataPoints()[25].rawValue) > 0 ? Number(Number(slice.dataPoints()[25].rawValue).toFixed(0)) : null,
                        "RV% of List Price": Number(slice.dataPoints()[1].rawValue) > 0 && Number(slice.dataPoints()[25].rawValue) > 0 ? (100 * (Number(slice.dataPoints()[25].rawValue) / Number(slice.dataPoints()[1].rawValue))).toFixed(1) + "%" : null,
                        "Total Credit Charges": Number(slice.dataPoints()[43].rawValue) > 0 ? Number(Number(slice.dataPoints()[43].rawValue).toFixed(0)) : null,
                        "Finance/OEM Contribution": Number(slice.dataPoints()[42].rawValue) > 0 ? Number(Number(slice.dataPoints()[42].rawValue).toFixed(0)) : null,
                        "Total Payable": Number(slice.dataPoints()[44].rawValue) > 0 ? Number(Number(slice.dataPoints()[44].rawValue).toFixed(0)) : null,
                        "Web Rental includes":
                            Object.entries(services)
                                .filter((m) => m[1] === 1)
                                .map((m) => m[0])
                                .join(", ") || "No Services",
                        Comments: slice.sliceTitles()[15],
                        Url: slice.sliceTitles()[16],
                        Picture: slice.sliceTitles()[20],
                        "Target Deposit": selectedAdjDeposit === "Total Payable by month" ? "Total Payable" : Number(selectedAdjDeposit),
                        Adjustements: "",
                        // Dynamic services calculation
                        ...Object.entries(kpiSettings).reduce((acc, [flag, val]) => {
                            return {
                                ...acc,
                                [settingsAliases[flag][ctryid] || settingsAliases[flag]["default"]]: calculateAdjustment(slice, flagServiceM[flag].measureIndex, val, IncServices),
                            };
                        }, {}),
                        "Deposit Adjustement": Number(slice.dataPoints()[2].rawValue) > 0 ? Number(Number(slice.dataPoints()[2].rawValue).toFixed(0)) : null,
                        "Extrapolated from": Extrapfrom(slice.sliceTitles()[0])[1] ?? null,
                        "Adjusted Rental": Number(slice.dataPoints()[23].rawValue) > 0 ? Number(Number(slice.dataPoints()[23].rawValue).toFixed(0)) : null,
                        "Total monthly payments": Number(slice.dataPoints()[23].rawValue) > 0 ? Number(slice.sliceTitles()[14].split("/")[0]) * Number(Number(slice.dataPoints()[23].rawValue).toFixed(0)) : null,
                        "Leasing Factor": 100 * Number(slice.dataPoints()[24].rawValue) > 0 ? Number((100 * Number(slice.dataPoints()[24].rawValue)).toFixed(2)) : null,
                    };
                });
                return flat;
            }
        }
        return [];
    }, [result, status, selectedAdjDeposit, kpiSettings, ctryid, getCustlabelByCustId, IncServices]);

    useEffect(() => {
        setData(processedData);
    }, [processedData]);

    const handleExport = useCallback(() => {
        const transposeddata = transposeArrayOfObjects(data);
        const ws = utils.json_to_sheet(transposeddata, { skipHeader: true });
        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, "Sheet1");
        writeFile(wb, "Detailed View Table.xlsx");
    }, [data]);

    const headers = useMemo(() => {
        if (data.length > 0) {
            const keys = Object.keys(data[0]);
            let standardHeaders = keys.slice(1, 25);
            let publishedHeaders = keys.slice(25, 40);
            let adjustedHeaders = keys.slice(40);
            if (!showspecs) {
                standardHeaders = standardHeaders.filter((value) => {
                    return !keys.slice(keys.indexOf("Specifications") + 1, keys.indexOf("Product Type")).includes(value);
                });
            }
            if (!showoffers) {
                publishedHeaders = publishedHeaders.filter((value) => {
                    return !keys.slice(keys.indexOf("Offer details") + 1, keys.indexOf("Web Rental includes")).includes(value);
                });
            }
            if (!showadj) {
                adjustedHeaders = adjustedHeaders.filter((value) => {
                    return !keys.slice(keys.indexOf("Adjustements") + 1, keys.indexOf("Extrapolated from")).includes(value);
                });
            }
            return [standardHeaders, publishedHeaders, adjustedHeaders];
        }
        return [[], [], []];
    }, [data, showadj, showoffers, showspecs]);

    const syncScroll = useCallback((src, target) => {
        if (src.current && target.current) {
            target.current.scrollLeft = src.current.scrollLeft;
        }
    }, []);

    const handleContentScroll = useCallback(() => syncScroll(contentRef, scrollRef), [syncScroll, contentRef, scrollRef]);
    const handleScrollScroll = useCallback(() => syncScroll(scrollRef, contentRef), [syncScroll, contentRef, scrollRef]);

    useEffect(() => {
        const contentElement = contentRef.current;
        const scrollElement = scrollRef.current;

        if (contentElement && scrollElement) {
            setContentWidth(contentElement.scrollWidth);
            contentElement.addEventListener("scroll", handleContentScroll);
            scrollElement.addEventListener("scroll", handleScrollScroll);

            return () => {
                contentElement.removeEventListener("scroll", handleContentScroll);
                scrollElement.removeEventListener("scroll", handleScrollScroll);
            };
        }
    }, [handleContentScroll, handleScrollScroll, contentRef, scrollRef, data]);

    const handleSyncData = useCallback(() => {
        const newFilter = currentFilter.slice(0, 6).concat(currentFilter.slice(23));
        setCurrentFilter(newFilter);
    }, [currentFilter]);

    return (
        <>
            {status !== "error" && (
                <div className="relative">
                    <RecordSwitchParameter className={status === "loading" ? `opacity-40 select-none pointer-events-none` : ""} />
                </div>
            )}
            {status === "loading" && (
                <div>
                    <LoadingComponent className="my-28" />
                </div>
            )}
            {status === "error" && (
                <div>
                    <ErrorComponent />
                </div>
            )}
            {status === "success" && data.length > 0 && (
                <div className="my-8">
                    {!selectedVehicles.every((selected) => data.some((item) => item["Experteye Code"] === selected.Version)) && (
                        <div className="flex items-center gap-2">
                            <span className="block sm:inline text-steel font-medium">Your filters are not synced with the selected vehicles !</span>
                            <button onClick={handleSyncData} className="px-2 py-1.5 text-sm font-medium bg-indigo-500 text-white rounded-md flex items-center hover:opacity-80">
                                <BiSync className="w-5 h-5" />
                                Sync data
                            </button>
                        </div>
                    )}
                    <>
                        <div ref={scrollRef} className="overflow-x-scroll h-5">
                            <div style={{ width: `${contentWidth}px` }} />
                        </div>
                        <div ref={contentRef} onScroll={handleContentScroll} className="mt-2 shadow-inner overflow-scroll scrollbar-hide">
                            <table className="table-auto w-full min-w-max h-4/5 overflow-auto">
                                <thead className="sticky top-0 z-10">
                                    <tr>
                                        <th className="w-[48px] border" />
                                        <th className="sticky left-0 border w-[200px] bg-white text-xs text-left px-4">Make</th>
                                        {data.length > 0 &&
                                            data.map((item, index) => {
                                                return (
                                                    <th key={index} className={`border border-gray-200 w-[200px] px-4 py-2 text-xs font-semibold ${index % 2 === 0 ? "bg-gray-200" : ""}`}>
                                                        {item.Make}
                                                    </th>
                                                );
                                            })}
                                    </tr>
                                </thead>
                                <tbody>
                                    {/* Standard Rows  */}
                                    <td rowSpan={headers[0]?.length + 1} className="border border-gray-200 font-bold left-0 bg-white max-w-[24px]"></td>
                                    {headers[0]?.map((header, index) => {
                                        if (header === "Specifications") {
                                            return (
                                                <tr key={index}>
                                                    <td className="border text-gray-400 bg-white px-4 py-2 text-left text-xs sticky font-semibold left-0 cursor-pointer" onClick={() => setShowspecs(!showspecs)}>
                                                        {showspecs ? "( - )" : "( + )"} Specifications
                                                    </td>
                                                    {data.map((item, itemindex) => {
                                                        return (
                                                            <td key={itemindex} className={`border w-48 ${itemindex % 2 === 0 ? "bg-gray-200" : ""}`}>
                                                                {item[header]}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            );
                                        }
                                        return (
                                            <tr key={index}>
                                                <td className={`border border-gray-200 pr-4 ${Tabheaders.includes(header) ? "pl-12" : "pl-4"} text-left text-xs sticky font-semibold left-0 bg-white`}>{header}</td>
                                                {data.map((item, itemindex) => {
                                                    return (
                                                        <td key={itemindex} className={`border border-gray-200 ${itemindex % 2 === 0 ? "bg-gray-200" : ""} w-48 max-w-xs px-4 text-center ${typeof item[header] === "string" && item[header]?.split(" ").length === 1 ? "break-words" : ""}`}>
                                                            {item[header]}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })}
                                    {/* Published Rows */}
                                    <td rowSpan={headers[1]?.length + 1} className="bg-indigo-700 max-w-[24px]">
                                        <span className="text-white text-base inline-block transform -rotate-90 origin-center">Published</span>
                                    </td>
                                    {headers[1]?.map((header, index) => {
                                        if (header === "Offer details") {
                                            return (
                                                <tr key={index}>
                                                    <td className="border px-4 py-2 text-left text-xs sticky font-semibold left-0 bg-white text-gray-400 cursor-pointer" onClick={() => setShowoffers(!showoffers)}>
                                                        {showoffers ? "( - )" : "( + )"} Offer details
                                                    </td>
                                                    {data.map((item, itemindex) => {
                                                        return (
                                                            <td key={itemindex} className={`border ${itemindex % 2 === 0 ? "bg-gray-200" : ""} w-48`}>
                                                                {item[header]}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            );
                                        }
                                        return (
                                            <tr key={index}>
                                                <td className={`border border-gray-200 pr-4 ${Tabheaders.includes(header) ? "pl-12" : "pl-4"} text-left text-xs sticky font-semibold left-0 bg-white`}>{header}</td>
                                                {data.map((item, itemindex) => {
                                                    return (
                                                        <td key={itemindex} className={`${itemindex % 2 === 0 ? "bg-gray-200" : ""} border border-gray-200 w-48 max-w-xs px-4 text-center`}>
                                                            {header in columnIcon ? (
                                                                item[header] ? (
                                                                    <center>
                                                                        {header === "Url" ? (
                                                                            <a href={item[header]} className="text-steel" target="_blank" rel="noreferrer">
                                                                                {React.createElement(columnIcon[header])}
                                                                            </a>
                                                                        ) : (
                                                                            React.createElement(columnIcon[header], { className: "text-steel hover:text-blue-600 w-3.5 h-3.5", onClick: () => setSelectedPicURL(item[header]) })
                                                                        )}
                                                                    </center>
                                                                ) : null
                                                            ) : (
                                                                item[header]
                                                            )}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })}
                                    {/* Adjustments Rows */}
                                    <td rowSpan={headers[2]?.length + 1} className="bg-indigo-500 max-w-[24px]">
                                        <span className="text-white text-base inline-block transform -rotate-90 origin-center">Adjusted</span>
                                    </td>
                                    {headers[2]?.map((header, index) => {
                                        if (header === "Adjustements") {
                                            return (
                                                <tr key={index}>
                                                    <td className="border  px-4 py-2 text-left text-gray-400 text-xs sticky font-semibold left-0 bg-white cursor-pointer" onClick={() => setShowadj(!showadj)}>
                                                        {showadj ? "( - )" : "( + )"} Adjustements
                                                    </td>
                                                    {data.map((item, itemindex) => {
                                                        return (
                                                            <td key={itemindex} className={`${itemindex % 2 === 0 ? "bg-gray-200" : ""} border w-48`}>
                                                                {item[header]}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            );
                                        }
                                        return (
                                            <tr key={index}>
                                                <td className={`border border-gray-200 pr-4 ${Tabheaders.includes(header) ? "pl-12" : "pl-4"} text-left text-xs sticky font-semibold left-0 bg-white`}>{header}</td>
                                                {data.map((item, itemindex) => {
                                                    return (
                                                        <td key={itemindex} className={`${itemindex % 2 === 0 ? "bg-gray-200" : ""} border border-gray-200 w-48 max-w-xs px-4 text-center`}>
                                                            {item[header]}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <div className="my-4">
                            <Exportbtn onclick={handleExport} />
                        </div>
                    </>
                    <span className="text-xs font-medium">{footnote}</span>
                </div>
            )}
            {selectedPicURL && <ImageViewer url={selectedPicURL} selectPicHandler={setSelectedPicURL} />}
        </>
    );
};

export default MakeModelDetailedView;
